import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import playButtonIcon from '@/assets/images/playButton.svg';
import Workout from '@/assets/images/workout.jpg';
import { Button } from '@/components/ui/button';
import { ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import HowItWorks from '../../home/components/HowItWorks';
import BuySubsctription from '../../subscription/components/BuySubsctription';

interface FirstSessionCardProps {
  setOpen: (program?: string) => void;
}

const FirstSessionCard = ({ setOpen }: FirstSessionCardProps) => {
  const [selectedProgram, setSelectedProgram] = useState<string>('fitvarsity');
  const [howItWorkOpen, setHowItWorkOpen] = useState(false);
  const [buySubscriptionOpen, setBuySubscriptionOpen] = useState(false);

  return (
    <div className='w-[538px] bg-[#262633] rounded-xl overflow-hidden'>
      {/* Background Image with Gradient Overlay */}
      <div className='relative h-[260px]'>
        <img src={Workout} alt='' className='w-full h-full object-cover' />
        <div
          className='absolute inset-0'
          style={{
            background:
              'linear-gradient(180deg, rgba(38, 38, 51, 0) 42%, rgba(38, 38, 51, 1) 100%)',
          }}
        />
      </div>

      <div className='flex flex-col p-5 gap-[21px]'>
        {/* Header Section */}
        <div className='flex flex-col gap-2'>
          <h2 className='text-white font-bold text-[26px] leading-[36px] tracking-[0.02em]'>
            Find the Right Program for You
          </h2>
          <p className='text-white font-medium text-base leading-[22px]'>
            Choose from our expert-designed fitness programs tailored to your goals and lifestyle.
          </p>
        </div>

        {/* Programs Section */}
        <div className='bg-[rgba(87,87,87,0.31)] rounded-xl p-2.5 flex flex-col gap-2.5'>
          {/* FitVarsity */}
          <div
            className={`flex flex-col gap-1.5 items-start cursor-pointer transition-all p-2 rounded-lg ${
              selectedProgram === 'fitvarsity'
                ? 'bg-white/10 border border-white/20'
                : 'hover:bg-white/5'
            }`}
            onClick={() => setSelectedProgram('fitvarsity')}
          >
            {/* Program Header */}
            <div className='flex items-center gap-1 justify-center'>
              <div className='w-[14px] h-[14px] flex items-center justify-center'>
                <img src={FitVarsityIcon} alt='FitVarsity' className='w-full h-full' />
              </div>
              <span className='text-white font-semibold text-xs leading-[17px] text-center'>
                FitVarsity
              </span>
            </div>
            {/* Program Description */}
            <p className='text-[#C5C8D0] text-[10px] leading-[14px] text-left'>
              Training for ages 13–18 with sport-specific workouts to boost your game.
            </p>
          </div>

          {/* Divider */}
          <div className='w-full h-px bg-white/30' />

          {/* FitPrime */}
          <div
            className={`flex flex-col gap-1.5 items-start cursor-pointer transition-all p-2 rounded-lg ${
              selectedProgram === 'fitprime'
                ? 'bg-white/10 border border-white/20'
                : 'hover:bg-white/5'
            }`}
            onClick={() => setSelectedProgram('fitprime')}
          >
            {/* Program Header */}
            <div className='flex items-center gap-1 justify-center'>
              <div className='w-[14px] h-[14px] flex items-center justify-center'>
                <img src={FitPrimeIcon} alt='FitPrime' className='w-full h-full' />
              </div>
              <span className='text-white font-semibold text-xs leading-[17px] text-center'>
                FitPrime
              </span>
            </div>
            {/* Program Description */}
            <p className='text-[#C5C8D0] text-[10px] leading-[14px] text-left'>
              Personal training just for you — Yoga, Strength, Cardio & more. Pay as you go.
            </p>
          </div>

          {/* Divider */}
          <div className='w-full h-px bg-white/30' />

          {/* FitCurve - Row Layout */}
          <div className='flex items-stretch gap-2.5'>
            <div
              className={`flex flex-col gap-1.5 items-start cursor-pointer transition-all p-2 rounded-lg flex-1 ${
                selectedProgram === 'fitcurve'
                  ? 'bg-white/10 border border-white/20'
                  : 'hover:bg-white/5'
              }`}
              onClick={() => setSelectedProgram('fitcurve')}
            >
              {/* Program Header */}
              <div className='flex items-center gap-1 items-start justify-center'>
                <div className='w-[14px] h-[14px] flex items-center justify-center'>
                  <img src={FitCurveIcon} alt='FitCurve' className='w-full h-full' />
                </div>
                <span className='text-white font-semibold text-xs leading-[17px] text-center'>
                  FitCurve
                </span>
              </div>
              {/* Program Description */}
              <p className='text-[#C5C8D0] text-[10px] leading-[14px] text-left'>
                Lower body sculpting for visible results. Achieve stronger curves in weeks.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex flex-col gap-2.5'>
          {/* Schedule New Session Button - Full Width */}
          <Button
            onClick={() => setOpen(selectedProgram)}
            id='first-schedule-session-btn'
            className='w-full h-12 text-black font-bold text-sm leading-[20px] tracking-[0.02em] flex items-center justify-center gap-[14px] px-2.5'
            style={{
              background:
                'linear-gradient(98deg, rgba(248, 177, 7, 1) 0%, rgba(239, 232, 115, 1) 100%)',
            }}
          >
            <Plus className='w-6 h-6' />
            Schedule New Session
          </Button>

          {/* How it Works Button - Matching ProgramSlider */}
          <div className='flex justify-center'>
            <Button
              id='how-it-works-btn'
              size='sm'
              variant='ghost'
              className=' w-full text-white bg-[#343840] font-medium flex items-center justify-center px-5 py-2  h-auto'
              onClick={() => setHowItWorkOpen(true)}
            >
              <img src={playButtonIcon} height={24} width={24} className='mr-2' />
              How it Works
            </Button>
          </div>
        </div>

        {/* Pay as you Go Section - Matching ProgramSlider */}
        <div className='flex justify-center'>
          <Button
            id='pay-as-you-go-btn'
            size='sm'
            variant='destructive'
            className='text-white bg-transparent hover:bg-transparent hover:text-gray-400 font-medium flex items-center justify-center px-5 py-2 w-56 h-auto'
            onClick={() => setBuySubscriptionOpen(true)}
          >
            Pay as you Go <ChevronRight />
          </Button>
        </div>
      </div>

      {/* Modals */}
      {howItWorkOpen && <HowItWorks open={howItWorkOpen} setOpen={setHowItWorkOpen} />}

      {buySubscriptionOpen && (
        <BuySubsctription
          open={buySubscriptionOpen}
          handleClose={() => setBuySubscriptionOpen(false)}
          selectedProgram={selectedProgram}
        />
      )}
    </div>
  );
};

export default FirstSessionCard;
